import { HttpBackend, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import {
  INGXLoggerConfig,
  INGXLoggerMetadata,
  NGXLoggerServerService,
  NgxLoggerLevel
} from 'ngx-logger';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment.prod';
import { SessionStorageService } from '../storage-service/session-storage.service';
import { ClientLogRequest } from './client-log.models';
import { LoginResponse } from '../../../data-services/api-models/config-service-models';
import { findLogMasterData } from '../../../data-services/api-models/log-reporter-metadata';
// import { findLogMasterData } from '../../../data-services/api-models/custom-models';

@Injectable()
export class ClientLogServerService extends NGXLoggerServerService {
  private static readonly USER_SESSION_STORAGE_KEY = 'userResponse';

  constructor(
    httpBackend: HttpBackend,
    ngZone: NgZone,
    private readonly sessionStorageService: SessionStorageService
  ) {
    super(httpBackend, ngZone);
  }

  protected override alterHttpRequest(
    httpRequest: HttpRequest<unknown>
  ): HttpRequest<unknown> {
    const session = this.readLoginResponse();
    let headers = httpRequest.headers ?? new HttpHeaders();

    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('X-Client-Application', 'tms-pnrd');
    headers = headers.set('X-Client-Version', environment.production ? 'prod' : 'dev');

    const requestId = session?.sessionId?.trim() || 'AppPrelogin';
    headers = headers.set('RequestId', requestId);

    if (session?.securityToken) {
      headers = headers.set('Authorization', `Bearer ${session.securityToken}`);
    }

    return httpRequest.clone({ headers });
  }

  protected override customiseRequestBody(
    metadata: INGXLoggerMetadata | INGXLoggerMetadata[]
  ): ClientLogRequest | ClientLogRequest[] {
    if (Array.isArray(metadata)) {
      return metadata.map((item) => this.buildRequest(item));
    }

    return this.buildRequest(metadata);
  }

  protected override sendToServerAction(
    metadata: INGXLoggerMetadata | INGXLoggerMetadata[],
    config: INGXLoggerConfig
  ): void {
    const secureMetadata = (item: INGXLoggerMetadata): INGXLoggerMetadata => {
      const securedItem = { ...item };
      securedItem.additional = securedItem.additional
        ? this.secureAdditionalParameters(securedItem.additional)
        : undefined;
      securedItem.message = this.secureMessage(securedItem.message);
      return securedItem;
    };

    const requestBody = this.customiseRequestBody(
      Array.isArray(metadata) ? metadata.map((item) => secureMetadata(item)) : secureMetadata(metadata)
    );

    const headers = config.customHttpHeaders || new HttpHeaders();
    const logOnServerAction = () => {
      this.logOnServer(config.serverLoggingUrl!, requestBody, {
        headers,
        params: config.customHttpParams || new HttpParams(),
        responseType: config.httpResponseType || 'json',
        withCredentials: config.withCredentials || false,
      }).pipe(
        catchError((err) => {
          // Logging delivery must never break the login flow or show end-user error popups.
          console.error('NGXLogger: Failed to log on server', err);
          return EMPTY;
        })
      ).subscribe();
    };

    if (config.serverCallsOutsideNgZone === true) {
      if (!this.ngZone) {
        console.error('NGXLogger: NgZone is not provided and serverCallsOutsideNgZone is set to true');
        return;
      }
      this.ngZone.runOutsideAngular(logOnServerAction);
      return;
    }

    logOnServerAction();
  }

  private buildRequest(metadata: INGXLoggerMetadata): ClientLogRequest {
    const session = this.readLoginResponse();
    const roles = session?.userRights?.roles ?? [];
    const offices = session?.userRights?.offices ?? [];
    const userDetails = session?.userDetails;
    const requestId = session?.sessionId?.trim() || 'AppPrelogin';
    const levelMaster = this.resolveLogLevelMaster(metadata.level);
    const sourceMaster = findLogMasterData('Source', 'Application');
    const loggerMaster = findLogMasterData('Logger', 'tms-pnrd');
    const messageTypeMaster = findLogMasterData('MessageType', 'Default');

    return {
      requestId,
      application: {
        name: 'tms-pnrd',
        version: '0.01',
        environment: environment.environment,
      },
      session: {
        requestId,
        sessionId: session?.sessionId,
        isAuthenticated: !!session?.securityToken,
        userId: userDetails?.id,
        userLoginId: userDetails?.userLoginId ?? undefined,
        userName: this.buildUserName(userDetails?.firstName, userDetails?.lastName),
        emailId: userDetails?.emailId ?? undefined,
        officeIds: offices.map((office: any) => office.id).filter((id: any): id is string => !!id),
        roleIds: roles.map((role: any) => role.id).filter((id: any): id is string => !!id),
        roleNames: roles
          .map((role: any) => role.description)
          .filter((name: any): name is string => !!name),
        isSuperUser: session?.userRights?.isSuperUser ?? false,
      },
      client: {
        url: this.getCurrentUrl(),
        path: this.getCurrentPath(),
        queryString: this.getCurrentQueryString(),
        userAgent: this.getNavigatorValue('userAgent'),
        language: this.getNavigatorValue('language'),
        timeZone: this.getTimeZone(),
        online: this.getOnlineStatus(),
      },
      log: {
        level: levelMaster?.id ?? metadata.level,
        levelId: levelMaster?.id ?? metadata.level,
        levelKeyName: levelMaster?.keyName ?? this.getLevelName(metadata.level),
        levelName: levelMaster?.description ?? this.getLevelName(metadata.level),
        sourceId: sourceMaster?.id,
        sourceKeyName: sourceMaster?.keyName,
        sourceName: sourceMaster?.description,
        loggerId: loggerMaster?.id,
        loggerKeyName: loggerMaster?.keyName,
        loggerName: loggerMaster?.description,
        messageTypeId: messageTypeMaster?.id,
        messageTypeKeyName: messageTypeMaster?.keyName,
        messageTypeName: messageTypeMaster?.description,
        timestamp: metadata.timestamp,
        message: this.toStringOrUndefined(metadata.message),
        additional: metadata.additional,
        fileName: metadata.fileName,
        lineNumber: metadata.lineNumber,
        columnNumber: metadata.columnNumber,
      },
    };
  }

  private readLoginResponse(): LoginResponse | undefined {
    return this.sessionStorageService.getItem<LoginResponse>(
      ClientLogServerService.USER_SESSION_STORAGE_KEY
    );
  }

  private resolveLogLevelMaster(level: number): LogMasterData | undefined {
    switch (level) {
      case NgxLoggerLevel.TRACE:
        return findLogMasterData('LogLevel', 'TRACE');
      case NgxLoggerLevel.DEBUG:
        return findLogMasterData('LogLevel', 'DEBUG');
      case NgxLoggerLevel.INFO:
        return findLogMasterData('LogLevel', 'INFO')
          ?? findLogMasterData('LogLevel', 'Information');
      case NgxLoggerLevel.LOG:
        return findLogMasterData('LogLevel', 'Information')
          ?? findLogMasterData('LogLevel', 'INFO');
      case NgxLoggerLevel.WARN:
        return findLogMasterData('LogLevel', 'WARN')
          ?? findLogMasterData('LogLevel', 'Warning');
      case NgxLoggerLevel.ERROR:
        return findLogMasterData('LogLevel', 'Error');
      case NgxLoggerLevel.FATAL:
        return findLogMasterData('LogLevel', 'Fatal');
      default:
        return undefined;
    }
  }

  private buildUserName(
    firstName?: string | undefined,
    lastName?: string | undefined
  ): string | undefined {
    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return fullName || undefined;
  }

  private getLevelName(level: number): string {
    switch (level) {
      case 0:
        return 'TRACE';
      case 1:
        return 'DEBUG';
      case 2:
        return 'INFO';
      case 3:
        return 'LOG';
      case 4:
        return 'WARN';
      case 5:
        return 'ERROR';
      case 6:
        return 'FATAL';
      case 7:
        return 'OFF';
      default:
        return `${level}`;
    }
  }

  private toStringOrUndefined(value: unknown): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  private getCurrentUrl(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return window.location.href;
  }

  private getCurrentPath(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return window.location.pathname;
  }

  private getCurrentQueryString(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return window.location.search || undefined;
  }

  private getNavigatorValue(key: 'userAgent' | 'language'): string | undefined {
    if (typeof navigator === 'undefined') {
      return undefined;
    }

    return navigator[key];
  }

  private getOnlineStatus(): boolean | undefined {
    if (typeof navigator === 'undefined') {
      return undefined;
    }

    return navigator.onLine;
  }

  private getTimeZone(): string | undefined {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  }
}


export interface LogMasterData {
  id?: number;
  category?: string | undefined;
  keyName?: string | undefined;
  description?: string | undefined;
}